import { useCallback, useEffect, useState } from "react";
import { Mail, Send, CheckCircle2, AlertTriangle, Loader2, RefreshCw, Server, Lock, AtSign } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function AdminSettings() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [testTo, setTestTo] = useState("");
    const [sending, setSending] = useState(false);

    const fetchStatus = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/email/status");
            setStatus(data);
            setTestTo((prev) => (prev ? prev : data?.to_admin || ""));
        } catch (e) {
            toast.error("Falhou: " + formatApiError(e?.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    async function sendTest() {
        if (!testTo) {
            toast.error("Indica um destinatário");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post("/admin/email/test", { to: testTo });
            toast.success(data?.message || "Email de teste enviado!");
        } catch (e) {
            toast.error("Falhou: " + formatApiError(e?.response?.data?.detail || e.message));
        } finally {
            setSending(false);
        }
    }

    return (
        <div className="space-y-4" data-testid="admin-settings">
            <header className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-heading text-xl lg:text-2xl font-extrabold text-primary leading-tight">Definições</h1>
                    <p className="text-xs text-muted-foreground mt-0.5">Configuração do servidor de email para notificações automáticas.</p>
                </div>
                <button onClick={fetchStatus} disabled={loading}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-1.5 text-xs font-semibold text-primary hover:bg-muted transition disabled:opacity-50">
                    {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Recarregar
                </button>
            </header>

            {/* Status card */}
            <section className="rounded-2xl border border-border bg-white p-6 lg:p-8">
                <div className="flex items-start gap-4">
                    <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${status?.configured ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                        {status?.configured ? <CheckCircle2 className="h-6 w-6" /> : <AlertTriangle className="h-6 w-6" />}
                    </div>
                    <div className="flex-1">
                        <h2 className="font-heading text-xl font-bold text-primary">
                            {status?.configured ? "SMTP configurado" : "SMTP não configurado"}
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            {status?.configured
                                ? "O sistema vai enviar email para o admin sempre que houver um novo pedido do simulador ou contacto."
                                : "Os pedidos continuam a ser guardados no backoffice mas nenhum email é enviado. Preenche as variáveis SMTP no ficheiro backend/.env e reinicia o backend."
                            }
                        </p>
                    </div>
                </div>

                {/* Config preview */}
                {status && (
                    <dl className="mt-6 grid gap-x-6 gap-y-3 sm:grid-cols-2 text-sm">
                        <ConfigRow icon={Server} label="Host" value={status.host || "—"} />
                        <ConfigRow icon={Server} label="Porta" value={status.port} />
                        <ConfigRow icon={AtSign} label="Username" value={status.username || "—"} />
                        <ConfigRow icon={Lock} label="Password" value={status.has_password ? "•••••• (definida)" : "—"} />
                        <ConfigRow icon={Mail} label="From" value={status.from || "—"} />
                        <ConfigRow icon={Mail} label="To (admin)" value={status.to_admin || "—"} />
                    </dl>
                )}

                {!status?.configured && (
                    <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
                        <p className="font-semibold mb-2">Como configurar:</p>
                        <ol className="space-y-1.5 list-decimal list-inside">
                            <li>Abre o ficheiro <code className="px-1.5 py-0.5 rounded bg-white text-xs">backend/.env</code></li>
                            <li>Preenche as variáveis: <code className="text-xs">SMTP_HOST</code>, <code className="text-xs">SMTP_PORT</code>, <code className="text-xs">SMTP_USERNAME</code>, <code className="text-xs">SMTP_PASSWORD</code>, <code className="text-xs">SMTP_FROM</code>, <code className="text-xs">SMTP_TO_ADMIN</code></li>
                            <li>Para Gmail usa uma <strong>App Password</strong> em <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">myaccount.google.com/apppasswords</a></li>
                            <li>Reinicia o backend: <code className="text-xs">sudo supervisorctl restart backend</code></li>
                            <li>Volta aqui e usa o &ldquo;Enviar email de teste&rdquo;</li>
                        </ol>
                    </div>
                )}
            </section>

            {/* Test email */}
            <section className="rounded-2xl border border-border bg-white p-6 lg:p-8">
                <div className="flex items-start gap-4">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-secondary/15 text-primary">
                        <Send className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-heading text-xl font-bold text-primary">Enviar email de teste</h2>
                        <p className="text-sm text-muted-foreground mt-1">Verifica que a configuração SMTP está a funcionar.</p>
                    </div>
                </div>

                <form
                    onSubmit={(e) => { e.preventDefault(); sendTest(); }}
                    className="mt-6 flex gap-3 flex-wrap"
                >
                    <input
                        type="email"
                        value={testTo}
                        onChange={(e) => setTestTo(e.target.value)}
                        placeholder="destinatario@exemplo.pt"
                        required
                        data-testid="smtp-test-to"
                        className="flex-1 min-w-[260px] rounded-md border border-border bg-white px-4 py-2.5 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-secondary"
                    />
                    <button
                        type="submit"
                        disabled={sending || !status?.configured}
                        data-testid="smtp-test-send"
                        className="inline-flex items-center gap-2 rounded-md bg-secondary px-5 py-2.5 text-sm font-bold text-primary shadow-sm hover:-translate-y-0.5 transition disabled:opacity-50 disabled:translate-y-0"
                    >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {sending ? "A enviar…" : "Enviar"}
                    </button>
                </form>
                {!status?.configured && (
                    <p className="mt-3 text-xs text-muted-foreground">Ativa a configuração SMTP para poder enviar.</p>
                )}
            </section>

            {/* Info card */}
            <section className="rounded-2xl border border-border bg-muted/40 p-6 lg:p-8">
                <h3 className="font-heading text-lg font-bold text-primary">Quando é que o email é enviado?</h3>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2"><span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-secondary" /> Sempre que um cliente submete uma <strong className="text-primary">configuração no Simulador</strong> (envia para o admin com o resumo)</li>
                    <li className="flex items-start gap-2"><span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-secondary" /> Sempre que alguém envia o formulário em <strong className="text-primary">/contacto</strong></li>
                    <li className="flex items-start gap-2"><span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-secondary" /> O email permite responder directamente (Reply-To = email do cliente)</li>
                </ul>
            </section>
        </div>
    );
}

function ConfigRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-4 py-2.5">
            <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
                <div className="text-sm text-primary font-mono truncate">{value || "—"}</div>
            </div>
        </div>
    );
}
